import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import UserManagement from "@/pages/UserManagement";
import AdminSettings from "@/pages/AdminSettings";
import PartnerDashboard from "@/pages/PartnerDashboard";
import BrightPage from "@/pages/BrightPage";
import BillingPage from "@/pages/BillingPage";
import ActivityLog from "@/pages/ActivityLog";
import AdvertisementsPage from "@/pages/AdvertisementsPage";
import SpringServePage from "@/pages/SpringServePage";
import AdServerCosts from "@/pages/AdServerCosts";
import PublisherDashboard from "@/pages/PublisherDashboard";
import Statements from "@/pages/Statements";
import ProductDashboard from "@/pages/ProductDashboard";
import ProductSourcePage from "@/pages/ProductSourcePage";
import ProductCompanies from "@/pages/ProductCompanies";
import ProductBilling from "@/pages/ProductBilling";
import PartnerPreview from "@/pages/PartnerPreview";
import { adminApi, productApi } from "@/lib/api";
import { AdminSidebar, InternalUserSidebar, PartnerSidebar, ProductSidebar, DashboardShell } from "@/components/Sidebar";

function AdminLayout() {
  return (
    <DashboardShell sidebar={<AdminSidebar />}>
      <Outlet />
    </DashboardShell>
  );
}

function InternalUserLayout() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return (
    <DashboardShell sidebar={<InternalUserSidebar />}>
      <Outlet context={{ userRole: "user", userId: user.user_id }} />
    </DashboardShell>
  );
}

function PartnerHome() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return user.company_type === "content_partner" ? <PublisherDashboard /> : <PartnerDashboard />;
}

function PartnerLayout() {
  const user = JSON.parse(localStorage.getItem("auth_user") || "{}");
  return (
    <DashboardShell sidebar={<PartnerSidebar />}>
      <Outlet context={{ userRole: "partner", userId: user.user_id }} />
    </DashboardShell>
  );
}

function ProductLayout() {
  return (
    <DashboardShell sidebar={<ProductSidebar />}>
      <Outlet />
    </DashboardShell>
  );
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment synchronously during render for session_id
  // This prevents race conditions — useEffect runs AFTER first render, too late
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return <AppRoutes />;
}

function AppRoutes() {
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("auth_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setAuthChecked(true);
  }, []);

  if (!authChecked) return null;

  const userRole = user?.role;

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={userRole === "admin" ? <AdminLayout /> : <Navigate to="/login" />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="partners" element={<Navigate to="/admin/users" />} />
        <Route path="billing" element={<BillingPage userRole="admin" userId={user?.user_id} />} />
        <Route path="statements" element={<Statements />} />
        <Route path="activity" element={<ActivityLog />} />
        <Route path="web-indexing" element={<BrightPage />} />
        <Route path="advertisements" element={<AdvertisementsPage />} />
        <Route path="springserve" element={<SpringServePage />} />
        <Route path="ad-costs" element={<AdServerCosts />} />
        <Route path="settings/password" element={<AdminSettings />} />
        <Route path="settings/bright" element={<Navigate to="/admin/web-indexing" />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="upload" element={<Navigate to="/admin/web-indexing" />} />
        <Route path="preview/:companyId" element={<PartnerPreview api={adminApi} backTo="/admin/users" />} />
      </Route>

      {/* Internal user routes */}
      <Route path="/user" element={userRole === "user" ? <InternalUserLayout /> : <Navigate to="/login" />}>
        <Route index element={<AdminDashboard />} />
        <Route path="billing" element={<BillingPage userRole="user" userId={user?.user_id} />} />
        <Route path="statements" element={<Statements />} />
        <Route path="ad-costs" element={<AdServerCosts />} />
      </Route>

      {/* Partner routes (SL partners + Content Partners) */}
      <Route path="/partner" element={userRole === "partner" ? <PartnerLayout /> : <Navigate to="/login" />}>
        <Route index element={<PartnerHome />} />
        <Route path="billing" element={<BillingPage userRole="partner" userId={user?.user_id} />} />
      </Route>

      {/* Product (learning) routes — real data with web-indexing money hidden, or admin-enabled sandbox */}
      <Route path="/product" element={userRole === "product" ? <ProductLayout /> : <Navigate to="/login" />}>
        <Route index element={<ProductDashboard />} />
        <Route path="billing" element={<ProductBilling />} />
        <Route path="web-indexing" element={<ProductSourcePage source="bright" />} />
        <Route path="advertisements" element={<ProductSourcePage source="aniview" />} />
        <Route path="springserve" element={<ProductSourcePage source="springserve" />} />
        <Route path="companies" element={<ProductCompanies />} />
        <Route path="preview/:companyId" element={<PartnerPreview api={productApi} backTo="/product/companies" />} />
        <Route path="ad-costs" element={<AdServerCosts api={productApi} readOnly />} />
        <Route path="activity" element={<ActivityLog api={productApi} />} />
      </Route>

      <Route path="*" element={
        <Navigate to={userRole === "admin" ? "/admin" : userRole === "user" ? "/user" : userRole === "partner" ? "/partner" : userRole === "product" ? "/product" : "/login"} />
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
